import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';

import { AlertCircleIcon } from '@/imports/ui/chakra/feather';

import { TICKET_MESSAGES, getWarningMessage } from './constants';

interface MultiTicketWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  audienceType: string;
  ticketName: string;
  quantity: number;
}

export const MultiTicketWarningModal: React.FC<MultiTicketWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  audienceType,
  ticketName,
  quantity,
}) => {
  const warningMessage = getWarningMessage(audienceType);
  const isRestrictive = !['Anyone', 'Any Logged-in User', 'Anyone with Ticket Link'].includes(audienceType);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent maxW="600px" borderRadius="xl">
        <ModalHeader pb={4} borderBottomWidth="1px" borderColor="#CCCCCC">
          <HStack spacing={3}>
            <Icon as={AlertCircleIcon} boxSize={6} color="#1676CE" />
            <Text fontSize="xl" fontWeight="bold" color="#333333">
              {TICKET_MESSAGES.MULTI_TICKET_WARNING.TITLE}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton size="lg" />
        
        <ModalBody py={6}>
          <VStack align="stretch" spacing={6}>
            {/* Main message */}
            <Box
              p={4}
              bg="#F5F5F5"
              borderRadius="md"
              borderLeftWidth="4px"
              borderLeftColor="#1676CE"
            >
              <Text textStyle="bodyStrong" color="#121212" lineHeight="1.6">
                {TICKET_MESSAGES.MULTI_TICKET_WARNING.PURCHASING(quantity, ticketName)}
              </Text>
            </Box>

            {/* Warning/Info Alert */}
            <Alert 
              status={isRestrictive ? "warning" : "info"} 
              borderRadius="md"
              variant="left-accent"
              py={4}
            >
              <AlertIcon boxSize={5} />
              <AlertDescription textStyle="compact" lineHeight="1.6" color="#333333">
                {warningMessage}
              </AlertDescription>
            </Alert>

            {/* Next step info */}
            <Box
              p={4}
              bg="#F5F5F5"
              borderRadius="md"
              borderWidth="1px"
              borderColor="#CCCCCC"
            >
              <Text textStyle="compact" color="#333333" lineHeight="1.6">
                {TICKET_MESSAGES.MULTI_TICKET_WARNING.NEXT_STEP_INFO}
              </Text>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter pt={4} borderTopWidth="1px" borderColor="#CCCCCC">
          <HStack spacing={3} w="full" justify="flex-end">
            <Button 
              variant="outline" 
              onClick={onClose}
              size="md"
              minW="120px"
            >
              {TICKET_MESSAGES.MULTI_TICKET_WARNING.CANCEL}
            </Button>
            <Button 
              colorScheme="primary" 
              onClick={onConfirm}
              size="md"
              minW="180px"
              fontWeight="semibold"
            >
              {TICKET_MESSAGES.MULTI_TICKET_WARNING.CONTINUE}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
